// Field limits and their messages, verbatim from the canvas of 18.08 — five drawn error frames,
// one per field (files/sow2/spec/arv.md section 4a lists the frame beside each).
//
// One table for both rows of the screen. Beds, Baths and Sqft belong to Search Criteria and Radius
// and Time to the filter row, and the two components would otherwise carry the same five sentences
// in two places — which is how a message drifts from the design in one of them and nobody notices,
// because each looks right on its own.
//
// The strings are his, character for character, including the comma in `10,000` and the full stops.
// Do not reword them here; if a limit changes it changes in Figma first.
export const FIELD_LIMITS = {
  // integer: true — assertRecomputeCriteriaBody (reilm-be modules/properties/validation.js) requires
  // Number.isInteger for beds, because real provider comp data is beds-integer-only (checked against
  // the cached sample) and a fractional value (3.5) has no corresponding real-world comp to match.
  // Reuses the existing range message rather than a new string for the integer case (fieldError
  // below): it reads slightly imprecisely for 3.5, but Beds already renders this exact message for
  // its drawn frame, and no drawn frame exists for a whole-number-only Beds, so a second, invented
  // sentence would be UI copy with nothing to check it against.
  // zeroAsUnset mirrors RECOMPUTE_CRITERIA_LIMITS' own exemption (reilm-be
  // modules/properties/validation.js): the service layer treats a literal 0 as unset and falls back
  // to property.details, so 0 has to keep passing this check for every field that reaches
  // recomputeArvService or it would be rejected here before it ever reaches the layer that
  // reinterprets it. It is a no-op for beds/baths/sqft, whose own min is already 0 — it matters only
  // for yearBuiltMin, whose min is 1800, and whose range check had no such exemption until mr-14
  // fix 3: clearing that field to 0 read as an out-of-range value rather than as "unset", which
  // disabled Search/Update Analysis the moment hasFieldError started reading this field too.
  beds:   { min: 0,   max: 15,     message: 'Enter a value between 0 and 15.', integer: true, zeroAsUnset: true },
  baths:  { min: 0,   max: 15,     message: 'Enter a value between 0 and 15.', zeroAsUnset: true },
  sqft:   { min: 0,   max: 10000,  message: 'Enter a value between 0 and 10,000.', zeroAsUnset: true },
  radius: { min: 0.4, max: 1,      message: 'Enter a value between 0.4 and 1.0 miles.' },
  time:   { min: 3,   max: 12,     message: 'Enter a value between 3 and 12 months.' },
  // Not one of the five drawn frames above — there is no canvas error state for Year Built of its
  // own (RECOMPUTE_CRITERIA_LIMITS' own comment in reilm-be modules/properties/validation.js says
  // so: "1800/2099 is the only bound that field carries anywhere today"). mr-14 fix 2 wires this
  // message into SearchCriteria.jsx's FieldError, reusing the same drawn pill Beds/Baths/Sqft
  // already have rather than inventing a sixth frame — the wording still carries no Figma
  // requirement of its own, only the range does.
  yearBuiltMin: { min: 1800, max: 2099, message: 'Year Built is out of range.', zeroAsUnset: true },
}

// Returns the message, or null when there is nothing to say.
//
// An empty field is not an error. Every one of these is optional — Beds and Baths draw the
// placeholder `Any`, and an empty Radius or Time is simply no bound — so treating blank as
// out-of-range would put a red message under a field the user has merely cleared on the way to
// typing something else.
//
// A value that is not a number is also not an error here, and that is not laxity: all five inputs
// are `type="number"`, so a browser hands back '' for anything it cannot parse and the case is
// unreachable through the UI. What IS reachable is a stored value of that shape — one production
// property still holds `sqftMin: "NaN"` from an old default — and the honest reading of a value we
// cannot compare is "no bound", the same as blank.
export function fieldError(field, value) {
  const limit = FIELD_LIMITS[field]
  if (!limit) return null
  if (value === '' || value == null) return null
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  if (limit.zeroAsUnset && n === 0) return null
  if (n < limit.min || n > limit.max) return limit.message
  if (limit.integer && !Number.isInteger(n)) return limit.message
  return null
}

// The four fields PATCH /:id/arv/criteria's body always carries together (Arv.jsx's
// buildRecomputePayload) — used there to gate queueRecompute so one invalid field cannot poison
// every subsequent recompute of the other three: the endpoint 400s the WHOLE body on any one bad
// value, and buildRecomputePayload always resends all four, so a value left invalid in one field
// (typed and never fixed, or loaded from a stored record predating one of these limits) fails every
// later edit's recompute too, silently, until that one field is corrected. `sqft` rather than
// `sqftMin` for the third check mirrors SearchCriteria.jsx's own sqftError line exactly, so the two
// can never disagree about which criteria value maps to which FIELD_LIMITS entry.
export function hasRecomputeFieldError(criteria) {
  return Boolean(
    fieldError('beds', criteria?.beds) ||
    fieldError('baths', criteria?.baths) ||
    fieldError('sqft', criteria?.sqftMin) ||
    fieldError('yearBuiltMin', criteria?.yearBuiltMin)
  )
}

// The number a filter should actually use, or null for "no bound".
//
// An out-of-range value filters nothing. The alternative — clamping it to the nearest legal value —
// looks tidier and lies: a user who typed 0.2 into Radius would get a table filtered at 0.4 while
// the field on screen reads 0.2 and the message says the value is wrong. Between showing the
// unfiltered table under a red message and showing a table filtered by a number nobody asked for,
// only the first is something the user can reason about.
export function boundValue(field, value) {
  if (fieldError(field, value) !== null) return null
  if (value === '' || value == null) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

// Comment Set #1, ARV 6.4, verbatim: "All fields should not be able to enter any special characters
// (ex. I can enter '6+' when the + symbol has no effect on any given field)". A bare `type="number"`
// input still accepts `e`, `E`, `+` and `-` as keystrokes — that is what makes exponent notation and
// a signed literal legal in a number input — and the browser only rejects the whole value on blur,
// after the character has already been typed and seen on screen. `.` stays legal: Radius needs
// values like 0.4-1.0 and Baths needs values like 3.5, both literal decimals, and every field this
// guards is a non-negative range (beds/baths 0-15, sqft 0-10,000, year 1800-2099, radius 0.4-1,
// time 3-12, price and sqft filters non-negative), which is the whole reason `-` can be blocked
// outright rather than allowed and then validated.
//
// Letting `.` through is not the same as making it safe, and no test here claims otherwise. `1.` or
// `1..` still reach the browser's own `type="number"` blur behaviour — `validity.badInput` true, a
// sanitized `''` on the controlled value — the same silent-empty state `blockSpecialNumberDrop`
// below is written up against, and it predates this guard entirely: `.` was never blocked before
// this MR either. Not fixed here because there is nothing to strip it from: a `badInput` input
// reports `.value === ''` with no way to read back the raw `1.` the user actually typed, so
// normalizing it on blur is not available to this function or to any handler downstream of it.
const BLOCKED_KEYS = new Set(['e', 'E', '+', '-'])

export function blockSpecialNumberKeys(e) {
  if (e.ctrlKey || e.metaKey) return
  if (BLOCKED_KEYS.has(e.key)) e.preventDefault()
}

export function blockSpecialNumberPaste(e) {
  const text = e.clipboardData?.getData('text') ?? ''
  if (/[eE+-]/.test(text)) e.preventDefault()
}

// logic-auditor, confirmed by Chrome CDP: dragging text onto any of these ten fields fires `drop`
// and `input` only — neither `keydown` nor `paste` — so the two guards above never see it. Dragging
// the literal `6+` from the requirement onto a field lands it un-caught: the field shows `6+` on
// screen (`validity.badInput` true), the controlled value the app actually holds reads `''` (a
// `type="number"` input reports an empty `.value` for text it cannot parse, and React's onChange
// never fires because that `.value` never changed), `fieldError` calls `''` "nothing to validate"
// rather than an error, no bound applies at the call site, and `onBlur` persists the empty value —
// all silent, no message anywhere on screen.
//
// Blocked outright rather than sanitized. Sanitizing would mean reading `dataTransfer`, validating
// it, and writing the result back through React's controlled value — real work for an interaction no
// requirement or workflow asks these fields to support in the first place; nobody drags a number into
// Beds or Radius.
export function blockSpecialNumberDrop(e) {
  e.preventDefault()
}
